import { Checkbox, DateInput, Input, MultiSelect } from '@folio/components';
import { type Dispatch, type SetStateAction, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  brokerControllerListDescendantBrokersQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import type {
  AssignedBrokerFilterConfig,
  BooleanFilterConfig,
  FilterConfig,
  TextInputFilterConfig,
} from 'korint-sdk/react';
import type { DateFilterConfig, Filter, SelectFilterConfig } from './types';
import { Group } from '@folio/components/ui/flex';

const ASSIGNED_BROKER_PAGE_SIZE = 50;
const ASSIGNED_BROKER_SEARCH_DEBOUNCE_MS = 300;

function isSelectFilter(filter: FilterConfig): filter is SelectFilterConfig {
  return filter.type === 'select';
}

function isDateFilter(filter: FilterConfig): filter is DateFilterConfig {
  return filter.type === 'date';
}

function isBooleanFilter(filter: FilterConfig): filter is BooleanFilterConfig {
  return filter.type === 'checkbox';
}

function isTextInputFilter(filter: FilterConfig): filter is TextInputFilterConfig {
  return filter.type === 'textInput';
}

function isAssignedBrokerFilter(filter: FilterConfig): filter is AssignedBrokerFilterConfig {
  return filter.type === 'assignedBrokerSelect';
}

function isDateRangeValue(value: Filter['value']): value is { start: string; end: string } {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('start' in value || 'end' in value)
  );
}

function ValueInput({
  filter,
  setFilters,
  index,
  filtersConfig,
}: {
  filter: Filter;
  setFilters: Dispatch<SetStateAction<Filter[]>>;
  index: number;
  filtersConfig: Record<string, FilterConfig>;
}) {
  const { t } = useTranslation();
  const selectedFilter = filtersConfig[filter.field];

  if (isBooleanFilter(selectedFilter)) {
    return (
      <Group gap={2}>
        <Checkbox
          id={filter.field}
          checked={filter.value as boolean}
          onCheckedChange={checked => {
            setFilters(currentFilters =>
              currentFilters.map((f, i) => (i === index ? { ...f, value: checked } : f)),
            );
          }}
        />
        <label htmlFor={filter.field} className="text-sm">
          {t(`filters.${filter.field}.label`)}
        </label>
      </Group>
    );
  }

  if (isDateFilter(selectedFilter)) {
    return (
      <Group align="stretch" gap={2}>
        <label className="grid flex-1 gap-1 text-xs text-muted-foreground">
          {t('filters.date.startPlaceholder')}
          <DateInput
            value={isDateRangeValue(filter.value) ? filter.value.start : undefined}
            onChange={newValue => {
              setFilters(currentFilters =>
                currentFilters.map((f, i) => {
                  if (i !== index) return f;
                  const currentValue = isDateRangeValue(f.value) ? f.value : { start: '', end: '' };
                  return {
                    ...f,
                    value: {
                      ...currentValue,
                      start: newValue || '',
                    },
                  };
                }),
              );
            }}
          />
        </label>
        <label className="grid flex-1 gap-1 text-xs text-muted-foreground">
          {t('filters.date.endPlaceholder')}
          <DateInput
            value={isDateRangeValue(filter.value) ? filter.value.end : undefined}
            onChange={newValue => {
              setFilters(currentFilters =>
                currentFilters.map((f, i) => {
                  if (i !== index) return f;
                  const currentValue = isDateRangeValue(f.value) ? f.value : { start: '', end: '' };
                  return {
                    ...f,
                    value: {
                      ...currentValue,
                      end: newValue || '',
                    },
                  };
                }),
              );
            }}
          />
        </label>
      </Group>
    );
  }

  if (isSelectFilter(selectedFilter)) {
    const resolveOption = (option: string) => {
      const labelKey =
        filter.field === 'productIds'
          ? `products.${option}.label`
          : filter.field === 'stopReason' || filter.field === 'terminationReason'
            ? `stopReasons.${option}`
            : filter.field === 'fraudStatus'
              ? `documentReview.fraud.status.${option}`
              : `${filter.field}.${option}`;
      return { label: t(labelKey), value: option };
    };

    const resolvedOptions = selectedFilter.options.map(resolveOption);

    const resolvedValue = Array.isArray(filter.value)
      ? (filter.value as ReadonlyArray<string>).map(resolveOption)
      : null;

    return (
      <MultiSelect
        placeholder={t(`filters.${filter.field}.inputPlaceholder`)}
        className="min-w-96"
        value={resolvedValue}
        options={resolvedOptions}
        onChange={(newValue: readonly { label: string; value: string }[]) => {
          setFilters(currentFilters =>
            currentFilters.map((f, i) =>
              i === index ? { ...f, value: newValue.map(v => v.value) } : f,
            ),
          );
        }}
        closeMenuOnSelect
      />
    );
  }

  if (isTextInputFilter(selectedFilter)) {
    return (
      <Input
        placeholder={t(`filters.${filter.field}.inputPlaceholder`)}
        className="min-w-96"
        value={(filter.value as string) || ''}
        onChange={e => {
          setFilters(currentFilters =>
            currentFilters.map((f, i) => (i === index ? { ...f, value: e.target.value } : f)),
          );
        }}
      />
    );
  }

  if (isAssignedBrokerFilter(selectedFilter)) {
    return (
      <AssignedBrokerSelect
        filter={filter}
        setFilters={setFilters}
        index={index}
        placeholder={t(`filters.${filter.field}.inputPlaceholder`)}
      />
    );
  }
}

type BrokerOption = { label: string; value: string };
type BrokerOptionGroup = { label: string; options: BrokerOption[] };

function AssignedBrokerSelect({
  filter,
  setFilters,
  index,
  placeholder,
}: {
  filter: Filter;
  setFilters: Dispatch<SetStateAction<Filter[]>>;
  index: number;
  placeholder: string;
}) {
  const { data: { brokerageFirmId } = {} } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, ASSIGNED_BROKER_SEARCH_DEBOUNCE_MS);

  const { data: brokers } = useQuery({
    ...brokerControllerListDescendantBrokersQueryOptions(brokerageFirmId ?? '', {
      count: ASSIGNED_BROKER_PAGE_SIZE,
      queryString: debouncedSearch || undefined,
    }),
    enabled: Boolean(brokerageFirmId),
    placeholderData: keepPreviousData,
  });

  const labelByUserId = useRef(new Map<string, BrokerOption>());

  const groupedOptions = useMemo<BrokerOptionGroup[]>(() => {
    const byFirm = new Map<string, BrokerOptionGroup>();
    for (const broker of brokers?.data ?? []) {
      const fullName = [broker.firstName, broker.lastName].filter(Boolean).join(' ');
      const option: BrokerOption = {
        label: fullName || broker.email || broker.userId,
        value: broker.userId,
      };
      labelByUserId.current.set(broker.userId, option);
      const group = byFirm.get(broker.brokerageFirmId) ?? {
        label: broker.brokerageFirmName,
        options: [],
      };
      group.options.push(option);
      byFirm.set(broker.brokerageFirmId, group);
    }
    return Array.from(byFirm.values());
  }, [brokers]);

  const selectedValues = Array.isArray(filter.value) ? (filter.value as ReadonlyArray<string>) : [];
  const resolvedValue = selectedValues.map(
    value => labelByUserId.current.get(value) ?? { label: value, value },
  );

  return (
    <MultiSelect
      placeholder={placeholder}
      className="min-w-96"
      value={resolvedValue}
      options={groupedOptions}
      inputValue={search}
      onInputChange={(value, action) => {
        if (action.action === 'input-change') {
          setSearch(value);
        }
      }}
      filterOption={null}
      onChange={(newValue: readonly BrokerOption[]) => {
        setFilters(currentFilters =>
          currentFilters.map((f, i) =>
            i === index ? { ...f, value: newValue.map(v => v.value) } : f,
          ),
        );
      }}
      closeMenuOnSelect
    />
  );
}

export default ValueInput;
